/*
 * Service availability — the single source of truth.
 *
 * The site previously described service coverage in three different,
 * mutually contradictory ways (Pop.services in network-data.ts, hard-coded
 * "all 18 PoPs" copy in FAQ / configurator / service pages, and the
 * per-city location pages). This file is now authoritative. Every surface
 * that describes what we sell where should read from here.
 *
 * Ground truth (SC Ops confirmation, 2026-09-10):
 *   IP Transit     — Sofia and Frankfurt only, 1G–400G
 *   Ethernet/L2VPN — all 18 PoPs
 *   DWDM           — all 18 PoPs
 *   Dark Fibre     — Sofia metro ONLY today. National and cross-
 *                    border routes are under construction or in
 *                    design. Route-level detail lives in
 *                    src/data/dark-fibre.ts; other Bulgarian PoPs
 *                    are `unavailable` for dark fibre until their
 *                    corridor is in service.
 *
 * Notes:
 *   * `Pop.services` in src/lib/network-data.ts is DERIVED from this file
 *     at module load. Do not hand-edit it — edit `serviceAvailability`
 *     below and the change flows there automatically.
 *   * Where availability is unknown, we use `null` and list it in the
 *     PR report so it can be resolved — we never infer.
 *   * PoP ids below are duplicated in `src/lib/network-data.ts`; that is
 *     intentional because network-data.ts imports from this file, so we
 *     cannot import ALL_POPS back here without a cycle. If a PoP is added
 *     or removed, update BOTH files. The build asserts nothing today,
 *     but a small consistency test could be added later.
 */

export type Delivery = "available" | "unavailable";

/**
 * Canonical service identifiers used across the codebase (URL slugs,
 * analytics events, configurator product ids all mirror this shape).
 */
export type ServiceId =
  | "ip-transit"
  | "ethernet-l2vpn"
  | "dwdm"
  | "dark-fibre";

/** Per-PoP delivery flags for the four productised services. */
export interface PopAvailability {
  "ip-transit": Delivery | null;
  "ethernet-l2vpn": Delivery | null;
  "dwdm": Delivery | null;
  "dark-fibre": Delivery | null;
}

/* Route-level dark fibre inventory lives in src/data/dark-fibre.ts.
 * That file is the single source of truth for planned routes,
 * ownership, and in-service dates. */

/**
 * Canonical PoP id list. Duplicated from src/lib/network-data.ts by design
 * (see header note) to break what would otherwise be a circular import.
 */
export const ALL_POP_IDS: readonly string[] = [
  // International (9)
  "sofia", "frankfurt", "istanbul", "athens", "london",
  "tbilisi", "dubai", "yerevan", "agarak",
  // Bulgarian Edge (9)
  "plovdiv", "stara-zagora", "burgas", "varna", "balchik",
  "dobrich", "montana", "pleven", "blagoevgrad",
];

/** Explicit ip-transit whitelist (ground truth). */
const IP_TRANSIT_POPS = new Set<string>(["sofia", "frankfurt"]);

/** Dark fibre in-service today is Sofia metro only. Planned
 *  routes to Kalotina and Komotini are tracked in
 *  src/data/dark-fibre.ts and do NOT flip PoPs to available here
 *  until they are in service. */
const DARK_FIBRE_POPS = new Set<string>(["sofia"]);

function seed(popId: string): PopAvailability {
  return {
    "ip-transit": IP_TRANSIT_POPS.has(popId) ? "available" : "unavailable",
    // Ground truth: Ethernet/L2VPN and DWDM are delivered from every PoP.
    "ethernet-l2vpn": "available",
    "dwdm": "available",
    // Dark Fibre: Sofia metro only today.
    "dark-fibre": DARK_FIBRE_POPS.has(popId) ? "available" : "unavailable",
  };
}

/** Availability map keyed by PoP id. */
export const serviceAvailability: Readonly<Record<string, PopAvailability>> =
  Object.freeze(
    Object.fromEntries(ALL_POP_IDS.map((id) => [id, Object.freeze(seed(id))])),
  );

/* ─── Helpers ─── */

export function isAvailable(popId: string, service: ServiceId): boolean {
  return serviceAvailability[popId]?.[service] === "available";
}

/** Return all PoP ids that deliver a given service. */
export function popsFor(service: ServiceId): readonly string[] {
  return ALL_POP_IDS.filter((id) => isAvailable(id, service));
}

/** Sales-friendly service labels for reuse in prose. */
export const SERVICE_LABELS: Record<ServiceId, { en: string; bg: string }> = {
  "ip-transit": { en: "IP Transit", bg: "IP Transit" },
  "ethernet-l2vpn": { en: "Ethernet / L2VPN", bg: "Ethernet / L2VPN" },
  "dwdm": { en: "DWDM Wavelengths", bg: "DWDM вълни" },
  "dark-fibre": { en: "Dark Fiber", bg: "Тъмно влакно" },
};

/**
 * Map the legacy `ctaService` prop used by service pages (a URL slug)
 * to a canonical ServiceId. Kept explicit so future renames are safe.
 */
export const CTA_SERVICE_TO_ID: Record<string, ServiceId> = {
  "ip-transit": "ip-transit",
  "ethernet-l2vpn": "ethernet-l2vpn",
  "wavelengths-dwdm": "dwdm",
  "dark-fiber": "dark-fibre",
};
